import { createTask, runAgent } from '@/api/agenticsearch'
import { nanoid } from 'nanoid'

export default {
  namespaced: true,
  state: {
    events: [],
    loading: false,
    error: null,
    eventSource: null,
    currentTaskId: null,
    latestSearch: {
      prompt: null,
      searchId: null,
      searchGrade: null,
      inputTokens: null,
      outputTokens: null,
    },
  },
  mutations: {
    addEvent(state, event) {
      state.events.push(event)
    },
    updateEventByType(state, { type, event }) {
      const existingIndex = state.events.findIndex(e => e.type === type)
      if (existingIndex !== -1) {
        state.events.splice(existingIndex, 1, event)
      } else {
        state.events.push(event)
      }
    },
    setLoading(state, loading) {
      state.loading = loading
    },
    setError(state, error) {
      state.error = error
    },
    setEventSource(state, eventSource) {
      state.eventSource = eventSource
    },
    setCurrentTaskId(state, taskId) {
      state.currentTaskId = taskId
    },
    clearResults(state) {
      state.events = []
      state.loading = false
      state.error = null
      state.currentTaskId = null
      state.latestSearch = {
        prompt: null,
        searchId: null,
        searchGrade: null,
        inputTokens: null,
        outputTokens: null,
      }
    },
    setLatestSearchPrompt(state, prompt) {
      state.latestSearch.prompt = prompt
    },
    clearLatestSearchPrompt(state) {
      state.latestSearch.prompt = null
    },
    setLatestSearch(state, updates) {
      state.latestSearch = { ...state.latestSearch, ...updates }
    },
    setTokenCounts(state, { inputTokens, outputTokens }) {
      state.latestSearch.inputTokens = inputTokens
      state.latestSearch.outputTokens = outputTokens
    },
  },
  actions: {
    async search({ commit, dispatch, rootGetters }, { query, addlPrompt, files, filters, searchGrade }) {
      await dispatch('clearResults')
      commit('setLoading', true)
      commit('setError', null)
      const searchId = nanoid()
      const updates = {
        prompt: query,
        searchId,
        searchGrade: searchGrade
      }
      commit('setLatestSearch', updates)
      if (addlPrompt) {
        query = `${addlPrompt}
        ---
        ${query}`
      }

      try {
        // Get user's state from user store, fallback to content_profile_id
        let userState = rootGetters['user/eventMetadata']?.ust || ''
        if (!userState) {
          userState = rootGetters['user/userContentProfileId'] || ''
        }
        const formData = buildSearchFormData(query, files, filters, userState)
        const taskResponse = await createTask(formData)
        const taskId = taskResponse.task_id
        commit('setCurrentTaskId', taskId)

        const eventSource = runAgent(
          taskId,
          getAgentEventCallback(commit),
          (error) => {
            commit('setError', error.message || 'An error occurred during search')
            commit('setLoading', false)
          },
          () => {
            commit('setLoading', false)
          }
        )

        commit('setEventSource', eventSource)
        return searchId
      } catch (error) {
        commit('setError', error.message || 'Failed to start search')
        commit('setLoading', false)
        throw error
      }
    },
    searchAssessments({ dispatch, commit }, { query, files, filters, searchGrade }) {
      const assessmentPrompt = `Search for assessments.
      Don't include assessments labeled as data tracking sheets unless explicitly requested.
      Don't include assessments referencing a specific standard code (e.g. RL.2.2, RI.4.1) in the title, description, PDF, or Google Doc link under any circumstance.
      Don't include assessments whose description contains "aligned to a specific standard".
      Don't include assessments whose title starts with "Quick Assessment:".
      Don't include assessments about braille unless the prompt specifically mentions the student is blind / low vision.
      When providing an assessment rationale, don't reference other assessments.`
      return dispatch('search', { query, addlPrompt: assessmentPrompt, files, filters, searchGrade })
    },
    clearResults({ commit, state }) {
      if (state.eventSource) {
        state.eventSource.close()
        commit('setEventSource', null)
      }
      commit('clearResults')
    },
  },
  getters: {
    events(state) {
      return state.events
    },
    contentResults(state, getters) {
      if (!state.events || !Array.isArray(state.events)) {
        return []
      }
      return state.events
        .filter(e => e.type === 'content_generated')
        .map(event => event.data)
        .filter(item => item && item.relevance_score >= 7)
        .sort((a, b) => (b.relevance_score || 0) - (a.relevance_score || 0))
    },
    assessmentResults(state, getters) {
      return getters.contentResults.filter(event =>
        event.type === 'assessment' &&
        !event.description?.toLowerCase().includes('aligned to a specific standard') && // filter out legacy assessments for specific standards
        !event.title?.toLowerCase().includes('present levels wizard') // filter out legacy PLW assessments
      )
    },
    hasAssessmentResults(state, getters) {
      return getters.assessmentResults.length > 0
    },
    loading(state) {
      return state.loading
    },
    error(state) {
      return state.error
    },
    currentPhase(state) {
      const phaseEvents = state.events.filter(e => e.type === 'phase_started')
      if (phaseEvents.length === 0) return null
      const latestPhase = phaseEvents[phaseEvents.length - 1]
      return latestPhase.data?.phase
    },
    latestSearchPrompt(state) {
      return state.latestSearch.prompt
    },
    latestTokenUsage(state) {
      return {
        inputTokens: state.latestSearch.inputTokens,
        outputTokens: state.latestSearch.outputTokens,
      }
    },
    latestSearchId(state) {
      return state.latestSearch.searchId
    },
    latestSearchGrade(state) {
      return state.latestSearch.searchGrade
    },
  },
}

function buildSearchFormData(query, files, filters, userState) {
  const formData = new FormData()

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files[]', file)
    })
  }

  const config = {
    prompt: query,
    model: 'gpt-4',
    temperature: 0.7,
  }
  formData.append('config', JSON.stringify(config))

  if (filters) {
    if (filters.studentName) {
      formData.append('studentName', filters.studentName)
    }
    if (filters.gradeLevels && filters.gradeLevels.length > 0) {
      formData.append('gradeLevel', filters.gradeLevels.join(','))
    }
    if (filters.subject) {
      formData.append('subject', filters.subject)
    }
    if (filters.category) {
      formData.append('category', filters.category)
    }
  }

  // Always include user's state if available
  if (userState) {
    formData.append('state', userState)
  }

  return formData
}

function getAgentEventCallback(commit) {
  return (data) => {
    try {
      const eventWithTimestamp = {
        ...data,
        timestamp: new Date().toISOString(),
      }

      if (data.type === 'generation_complete' && data.data) {
        commit('setTokenCounts', {
          inputTokens: data.data.input_tokens,
          outputTokens: data.data.output_tokens
        })
      }

      if (data.type === 'progress' && data.step !== undefined) {
        commit('updateEventByType', { type: 'progress', event: eventWithTimestamp })
      } else if (data.type === 'completed' || data.type === 'error') {
        commit('addEvent', eventWithTimestamp)
        commit('setLoading', false)
      } else {
        commit('addEvent', eventWithTimestamp)
      }
    } catch (error) {
      console.error('Error processing agent event:', error, 'Event data:', data)
      commit('setError', `Error processing event: ${error.message}`)
      commit('setLoading', false)
    }
  }
}
